/**
 * Shell entry point — async MFE manifest bootstrap.
 *
 * Execution order:
 *   1. Fetch /mfe-manifest.json (written by generate-manifest.cjs, served by CloudFront).
 *   2. Store result on window.__MFE_MANIFEST__ so webpack's Promise-based remote
 *      declarations (in webpack.config.js) can read live CloudFront URLs instead of
 *      the build-time fallback URLs.
 *   3. Only then import('./app/bootstrap') to mount the React tree.
 *      By the time React.lazy(() => import('auth/AuthRoutes')) is triggered,
 *      window.__MFE_MANIFEST__ is already populated.
 *
 * Zero-rebuild benefit:
 *   When auth (or any MFE) redeploys to a new CloudFront URL, CI/CD uploads a
 *   fresh mfe-manifest.json to Shell's S3 bucket only — Shell HTML/JS is NOT rebuilt.
 *
 * Fallback:
 *   If the fetch fails (404 in local dev, network error, etc.) the build-time
 *   remote URL baked into webpack.config.js is used transparently.
 *   Local dev always works without the manifest file being present.
 *
 * This file must NOT import any application code synchronously.
 * The dynamic import ensures Module Federation's shared scope is fully
 * initialised before the application bootstrap runs — preventing the
 * "Shared module is not available for eager consumption" runtime error.
 */
// ── Step 0: Broadcast Shell context marker ────────────────────────────────────
// Must execute BEFORE any MFE remote entry scripts load.  webpack's
// Promise-based remote factories run synchronously as the first module
// imported by bootstrap.tsx, so setting this flag here — before the dynamic
// import — guarantees every MFE can read it at its own initialisation boundary.
//
// MFEs use this flag to detect integration mode and skip duplicate provider
// initialisation:
//   const isStandalone = !window.__VISION_SHELL_CONTEXT__;
const shellContext = { version: '1.0', initialized: true };
window.__VISION_SHELL_CONTEXT__ = shellContext;
async function mountApp() {
    // ── Step 1: Fetch the MFE manifest ─────────────────────────────────────────
    // The file is served from the same CloudFront domain as shell (no CORS issues).
    // In local dev this 404s — the catch block silently falls through.
    try {
        const res = await fetch('/mfe-manifest.json', { cache: 'no-store' });
        if (res.ok) {
            const manifest = (await res.json());
            window.__MFE_MANIFEST__ = manifest;
            console.warn('[Shell] MFE manifest loaded:', manifest);
        }
    }
    catch {
        // Manifest unavailable — build-time remote URLs from webpack.config.js will be used.
        console.warn('[Shell] mfe-manifest.json unavailable, using build-time remote URLs');
    }
    // ── Step 2: Mount the React tree ───────────────────────────────────────────
    // webpack's Promise-based remotes now have access to window.__MFE_MANIFEST__
    // and will use the live CloudFront URL when any lazy MFE import is triggered.
    await import('./app/bootstrap');
}
mountApp().catch((err) => {
    console.error('[Shell] Bootstrap failed:', err);
});
export {};
